import { createStyles } from 'antd-style';

import {
  INPUT_BASE_HEIGHT,
  ThemeColors,
  ThemeFont,
  ThemeSpaces,
  hexToRgbaWithAlpha,
} from '../../../theme';
import {
  responsiveLgVsXl,
  responsiveMdVsLg,
  responsiveMdVsLgVsXl,
  responsiveXsVsSm,
  responsiveXsVsSmVsXl,
} from '../../../styles/responsiveMixins';
import { flex1, flexCenter, spaceBetween } from '../../../styles/mixins';
import { FA_SELECTOR } from '../../../styles/variables';

export const usePackageListCommonStyles = createStyles(({ token }) => ({
  newShipmentPackageHeader: {
    height: 40,
    backgroundColor: ThemeColors.COLOR_GREY9_6,
    padding: `0 ${ThemeSpaces.SPACE_NORM}px`,

    '.InputText': {
      height: 28,
    },
  },
  newShipmentPackageSection: {
    overflow: 'hidden',
  },
  newShipmentPackageSectionTitleSpaceXxs: {
    '> .NewShipmentPackage-Title': {
      marginBottom: ThemeSpaces.SPACE_XXS,
    },
  },
  newShipmentPackageSectionMergeWithPrev: {
    '&&': {
      overflow: 'visible',
      ...responsiveMdVsLg(
        { marginTop: ThemeSpaces.SPACE_NORM - ThemeSpaces.SPACE_NORM_15 },
        { marginTop: ThemeSpaces.SPACE_NORM - ThemeSpaces.SPACE_NORM_15 }
      ),
      '&:not(.has-two-lines) > .NewShipmentPackage-Title': {
        marginTop: 0,
      },
    },
  },
  newShipmentPackageSectionBody: {
    ...responsiveLgVsXl(
      {
        padding: `0 ${ThemeSpaces.SPACE_NORM}px ${ThemeSpaces.SPACE_NORM_15}px ${ThemeSpaces.SPACE_NORM}px`,
      },
      {
        padding: `0 ${ThemeSpaces.SPACE_NORM_20}px ${ThemeSpaces.SPACE_NORM_15}px ${ThemeSpaces.SPACE_NORM_20}px`,
      }
    ),

    '> *': {
      ...spaceBetween(ThemeSpaces.SPACE_NORM, 'vertical'),
    },
  },
  sectionBodyTwoLines: {
    ...responsiveLgVsXl(
      {
        marginLeft: -ThemeSpaces.SPACE_NORM,
        marginRight: -ThemeSpaces.SPACE_NORM,
      },
      {
        marginLeft: -ThemeSpaces.SPACE_NORM_20,
        marginRight: -ThemeSpaces.SPACE_NORM_20,
      }
    ),
  },
  newShipmentPackageTitle: {
    fontFamily: ThemeFont.FONT_CONDENSED,
    fontWeight: 'bold',
    lineHeight: '17px',
    height: 17,
    marginBottom: token.marginXS,
    whiteSpace: 'nowrap',

    ...responsiveMdVsLg(
      { fontSize: token.fontSize },
      { fontSize: token.fontSizeLG }
    ),
  },
  newShipmentPackageSubtitle: {
    fontFamily: ThemeFont.FONT_CONDENSED,
    fontSize: token.fontSizeLG,
    fontWeight: 'bold',
    color: ThemeColors.COLOR_GREY_BLUISH2,
    marginBottom: ThemeSpaces.SPACE_NORM,
  },
  newShipmentPackageSubsectionParent: {
    ...responsiveMdVsLg(
      {
        marginLeft: -ThemeSpaces.SPACE_NORM,
        marginRight: -ThemeSpaces.SPACE_NORM,
      },
      { marginLeft: 0, marginRight: 0 }
    ),
  },
  newShipmentPackageSubsection: {
    padding: `${token.paddingXS}px ${ThemeSpaces.SPACE_NORM}px 0 ${ThemeSpaces.SPACE_NORM}px`,
    ...responsiveMdVsLg(
      {
        paddingBottom: 0,
        borderWidth: 0,
        backgroundColor: 'transparent',
        borderRadius: 0,
        boxShadow: 'none',
      },
      {
        border: `1px solid ${token.colorWhite}`,
        backgroundColor: ThemeColors.COLOR_WHITE2,
        borderRadius: token.borderRadius,
        paddingBottom: ThemeSpaces.SPACE_NORM_15,
        boxShadow: `-1px -1px 2px ${hexToRgbaWithAlpha(ThemeColors.COLOR_GREY_BLUISH, 0.35)}, -1px -2px 0 ${token.colorWhite}, 6px 6px 19px ${hexToRgbaWithAlpha(ThemeColors.COLOR_GREY_BLUISH, 0.35)}`,
      }
    ),
  },
  newShipmentPackageSubsectionTitle: {
    fontSize: ThemeFont.FONT_SIZE_SM_PLUS,
    color: ThemeColors.COLOR_GREY_BLUISH2,
    fontFamily: ThemeFont.FONT_CONDENSED,
    fontWeight: 'bold',
    marginBottom: ThemeSpaces.SPACE_NORM0_5,
    textTransform: 'uppercase',
  },
  newShipmentPackage: {
    backgroundColor: ThemeColors.COLOR_WHITE6,

    '.DashedTwoLinesSeparator': {
      ...responsiveMdVsLg(
        {
          marginLeft: ThemeSpaces.SPACE_NORM,
          marginRight: ThemeSpaces.SPACE_NORM,
        },
        { marginLeft: 0, marginRight: 0 }
      ),
    },

    '.SymbolCol': {
      ...flexCenter(),
      minHeight: INPUT_BASE_HEIGHT,
    },

    '.DimensionSeparator': {
      fontSize: 22,
      fontWeight: 'bold',
      color: ThemeColors.COLOR_GREY_BLUISH,
      lineHeight: 1,

      ...responsiveMdVsLgVsXl(
        { margin: 0 },
        { margin: `0 -${ThemeSpaces.SPACE_XXS}px` },
        { margin: 0 }
      ),
    },

    '.DimensionalWeightResultLabel': {
      textAlign: 'right',
      fontSize: token.fontSizeSM,
      color: ThemeColors.COLOR_GREY_BLUISH,
      fontFamily: ThemeFont.FONT_CONDENSED,
      fontWeight: 'bold',
      textTransform: 'uppercase',
      marginBottom: ThemeSpaces.SPACE_XXS,
    },

    '.DimensionalWeightResult': {
      textAlign: 'right',
      fontSize: ThemeFont.FONT_SIZE_20,
      fontWeight: 500,
      color: ThemeColors.COLOR_GREY1_6,
      lineHeight: 1,

      '&.no-value': {
        fontWeight: 400,
        color: ThemeColors.COLOR_GREY_BLUISH,
      },
    },

    '.ant-radio-wrapper': {
      ...spaceBetween(ThemeSpaces.SPACE_NORM_15),
    },

    '&.editing': {
      '.NewShipmentPackage-Header': {
        ...responsiveMdVsLg({ height: 40 }, { height: 50 }),
      },

      '.NewShipmentPackage-Title': {
        ...responsiveMdVsLg(
          {
            height: 20,
            lineHeight: '20px',
            paddingLeft: ThemeSpaces.SPACE_NORM,
            fontSize: token.fontSize,
            marginTop: token.marginXS,
            marginBottom: token.marginXS,
            borderWidth: 0,
            boxShadow: 'none',
          },
          {
            height: 30,
            lineHeight: '30px',
            paddingLeft: ThemeSpaces.SPACE_NORM_20,
            fontSize: token.fontSizeLG,
            borderTop: `1px solid ${ThemeColors.COLOR_GREY7_8}`,
            borderBottom: `1px solid ${token.colorWhite}`,
            boxShadow: `0 1px 0 ${token.colorWhite}`,
            marginBottom: ThemeSpaces.SPACE_NORM,
          }
        ),
      },
    },
  },
  newShipmentPackageDeviceAddon: {
    '&::after': {
      content: "''",
      display: 'block',
      height: ThemeSpaces.SPACE_NORM,
      borderBottom: `1px solid ${ThemeColors.COLOR_GREY8_1}`,
      ...responsiveLgVsXl(
        {
          marginLeft: -ThemeSpaces.SPACE_NORM,
          marginRight: -ThemeSpaces.SPACE_NORM,
        },
        {
          marginLeft: -ThemeSpaces.SPACE_NORM_20,
          marginRight: -ThemeSpaces.SPACE_NORM,
        }
      ),
    },

    '&:last-child::after': {
      display: 'none',
    },
  },
  dimensionEquals: {
    ...flexCenter(),
    width: INPUT_BASE_HEIGHT,
    minWidth: INPUT_BASE_HEIGHT,
    height: INPUT_BASE_HEIGHT,
    borderRadius: token.borderRadius,
    backgroundColor: ThemeColors.COLOR_GREY_BLUISH3,

    ...responsiveMdVsLgVsXl(
      { margin: 0 },
      { margin: 0 },
      { margin: `0 ${token.marginSM}px` }
    ),

    [FA_SELECTOR]: {
      fontSize: 18,
      display: 'block',
      color: token.colorWhite,
    },
  },
  monitorDeviceIcon: {
    fontSize: 18,
    color: token.colorPrimary,
  },
  pieceListButton: {
    color: token.colorPrimary,
    fontSize: token.fontSize,
    fontWeight: 900,

    '&.ant-btn.ant-btn-default.ant-btn-lg': {
      color: token.colorPrimary,
      fontSize: token.fontSize,
      fontWeight: 900,
      width: 170,
      borderRadius: token.borderRadiusSM,
      borderColor: ThemeColors.COLOR_GREY_BLUISH3,
    },

    [FA_SELECTOR]: {
      marginLeft: token.marginXS,
    },
  },
  newShipmentPackageTbdCheckbox: {
    display: 'flex',
    alignItems: 'center',
    height: 32,
    borderBottom: `1px solid ${ThemeColors.COLOR_GREY7_8}`,

    '&.inline': {
      '.ant-form-item': {
        marginBottom: 0,
        marginRight: 0,
      },
      '.ant-form-item-horizontal .ant-form-item-label': {
        paddingBottom: 0,
      },
      height: 'auto',
      borderBottomWidth: 0,
      padding: 0,
      margin: 0,
    },

    '.ant-checkbox + span': {
      fontSize: ThemeFont.FONT_SIZE_SM_PLUS,
    },

    ...responsiveLgVsXl(
      {
        margin: `-${ThemeSpaces.SPACE_SM}px -${ThemeSpaces.SPACE_NORM}px ${ThemeSpaces.SPACE_SM}px -${ThemeSpaces.SPACE_NORM}px`,
        padding: `0 ${ThemeSpaces.SPACE_NORM}px`,

        '&.active': {
          marginBottom: -ThemeSpaces.SPACE_NORM_15,
        },
      },
      {
        margin: `-${ThemeSpaces.SPACE_NORM}px -${ThemeSpaces.SPACE_NORM_20}px ${ThemeSpaces.SPACE_NORM}px -${ThemeSpaces.SPACE_NORM_20}px`,
        padding: `0 ${ThemeSpaces.SPACE_NORM_20}px`,

        '&.active': {
          marginBottom: -ThemeSpaces.SPACE_NORM_15,
        },
      }
    ),
  },
  newShipmentPackageDescriptionRow: {
    '&, &:last-child': {
      marginBottom: -ThemeSpaces.SPACE_NORM,
    },
    '> .ant-col': {
      marginBottom: ThemeSpaces.SPACE_NORM,
    },
  },
  newShipmentPackageWeightCalculationRow: {
    flexWrap: 'nowrap',

    '&, .ant-checkbox-wrapper .ant-checkbox + span, .label': {
      lineHeight: '18px',

      ...responsiveXsVsSm(
        { fontSize: token.fontSizeSM },
        { fontSize: ThemeFont.FONT_SIZE_SM_PLUS }
      ),
    },

    ...responsiveXsVsSmVsXl(
      {
        '> .ant-col:first-child': { whiteSpace: 'pre-line' },
        '.ant-checkbox-wrapper .ant-checkbox + span': {
          paddingLeft: token.paddingXS,
        },
      },
      {
        '> .ant-col:first-child': { whiteSpace: 'pre-line' },
        '.ant-checkbox-wrapper .ant-checkbox + span': {
          paddingLeft: token.paddingSM,
        },
      },
      {
        '> .ant-col:first-child': { whiteSpace: 'initial' },
        '.ant-checkbox-wrapper .ant-checkbox + span': {
          paddingLeft: token.paddingXS,
        },
      }
    ),
  },
  newShipmentPackageVehicleType: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',

    '> .label': {
      marginRight: token.marginXS,
    },

    ...responsiveXsVsSmVsXl(
      {
        '> .ant-form-item': { width: 175 },
      },
      {
        '> .label': { width: 100 },
        '> .ant-form-item': { width: 210 },
      },
      {
        '> .label': { width: 'auto' },
        '> .ant-form-item': { width: 'auto', ...flex1() },
      }
    ),
  },
  newShipmentPackageErrorMessage: {
    color: token.colorPrimary,
    fontSize: 13,

    ...responsiveMdVsLg(
      {
        '.NewShipmentPackagesListSlider .SlidingContent-ContentTab > &.has-content':
          {
            marginTop: 0,
            marginBottom: ThemeSpaces.SPACE_NORM,
          },
      },
      {
        '&.has-content': {
          marginTop: -ThemeSpaces.SPACE_NORM,
        },
      }
    ),
  },
  newShipmentPackagePreviewBody: {
    ...responsiveLgVsXl(
      { padding: `0 ${ThemeSpaces.SPACE_NORM}px` },
      { padding: `0 ${ThemeSpaces.SPACE_NORM_20}px` }
    ),
  },
  newShipmentPackageBilledWeight: {
    fontSize: ThemeFont.FONT_SIZE_SM_PLUS,
    fontWeight: 'bold',
    fontFamily: ThemeFont.FONT_CONDENSED,
  },
  pieceListControls: {
    width: 20,
    height: 60,
    borderRadius: token.borderRadius,
    backgroundColor: token.colorPrimary,

    '.IconButton': {
      fontSize: 14,
      display: 'block',
      color: token.colorWhite,
    },
  },
  pieceListEnvelope: {
    backgroundColor: ThemeColors.COLOR_WHITE2,
    border: `2px solid ${token.colorWhite}`,
    borderRadius: token.borderRadiusSM,
    boxShadow: `0 -1px 0 ${token.colorWhite}, 0 1px 0 ${hexToRgbaWithAlpha(ThemeColors.COLOR_GREY_BLUISH, 0.25)}, 1px 1px 6px ${ThemeColors.COLOR_GREY8_1}`,

    '> .ant-row': {
      marginBottom: -ThemeSpaces.SPACE_NORM,
      '> .ant-col': {
        marginBottom: ThemeSpaces.SPACE_NORM,
      },
    },

    [`@media (min-width: ${token.screenLGMin}px)`]: {
      padding: `${ThemeSpaces.SPACE_NORM_15}px ${ThemeSpaces.SPACE_NORM_20}px`,
    },
    [`@media (min-width: ${token.screenMDMin}px) and (max-width: ${token.screenMDMax}px)`]:
      {
        padding: `${ThemeSpaces.SPACE_NORM}px ${ThemeSpaces.SPACE_NORM_20}px`,
      },
    [`@media (min-width: ${token.screenSMMin}px) and (max-width: ${token.screenSMMax}px)`]:
      {
        padding: ThemeSpaces.SPACE_NORM,
      },
    [`@media (max-width: ${token.screenXSMax}px)`]: {
      padding: `${ThemeSpaces.SPACE_NORM}px ${token.marginXS}px`,
    },
  },
}));
